import { memo, useCallback } from "react";

import type { S3Object } from "@/lib/gql/__generated__/graphql";
import { PlainFontFamily, PlainReservationComponentType, PlainSizeType } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";
import type * as FormModelType from "@/components/features/design/plain/model/type";

import { ExternalLink } from "@/components/ui/button/external-link";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";
import { SingleImageField } from "@/components/ui/form/fields/image-field/single";
import { SelectField } from "@/components/ui/form/fields/select-field";

import { Accordion } from "../accordion";
import { FormIslandSection } from "../island-section";
import { FontCard, LikeReservationComponentCard } from "./card";
import { InputWithColorPicker, RecommendColorList } from "./color-form";

const LogoSizeOptions: Array<FormOptionType> = [
	{ id: "S", label: "S", value: PlainSizeType.Small },
	{ id: "M", label: "M", value: PlainSizeType.Medium },
	{ id: "L", label: "L", value: PlainSizeType.Large },
];

type Props = {
	hotelID: string;

	// colorSetting
	backgroundColor: ForNodeType<string>;
	onChangeColorBackground: (value: string) => void;
	primaryColor: ForNodeType<string>;
	onChangeColorPrimary: (value: string) => void;
	fontColor: ForNodeType<string>;
	onChangeColorFont: (value: string) => void;
	onApplyRecommendationColors: (colorBackground: string, colorPrimary: string, colorFont: string) => void;

	// hotelInformationSetting
	logoImage: FormModelType.PlainSettingFormModel["ViewModel"]["logoImage"];
	logoSize: PlainSizeType;
	isShowHotelName: boolean;
	isShowHotelCatchCopy: boolean;
	isShowHotelLogo: boolean;
	isShowHotelKeyVisual: boolean;

	onChangeLogo: (value: S3Object) => void;
	onChangeLogoSize: (value: PlainSizeType) => void;
	onChangeIsShowHotelName: (value: boolean) => void;
	onChangeIsShowHotelCatchCopy: (value: boolean) => void;
	onChangeIsShowHotelLogo: (value: boolean) => void;
	onChangeIsShowHotelKeyVisual: (value: boolean) => void;

	// fontFamilySetting
	plainFontFamily: PlainFontFamily;
	onChangeFontFamily: (value: PlainFontFamily) => void;

	// reservationComponentSetting
	reservationComponentType: PlainReservationComponentType;

	onChangeReservationComponentType: (value: PlainReservationComponentType) => void;
};

export const BasicSettingForm = memo(
	({
		//
		hotelID,
		primaryColor,
		fontColor,

		// colorSetting
		backgroundColor,
		onChangeColorBackground,
		onChangeColorPrimary,
		onChangeColorFont,
		onApplyRecommendationColors,

		// hotelInformationSetting
		logoImage,
		logoSize,
		isShowHotelName,
		isShowHotelCatchCopy,
		isShowHotelLogo,
		isShowHotelKeyVisual,
		onChangeLogo,
		onChangeLogoSize,
		onChangeIsShowHotelName,
		onChangeIsShowHotelCatchCopy,
		onChangeIsShowHotelLogo,
		onChangeIsShowHotelKeyVisual,

		// fontFamilySetting
		plainFontFamily,
		onChangeFontFamily,

		// reservationComponentSetting
		reservationComponentType,
		onChangeReservationComponentType,
	}: Props) => {
		const handleChangeLogoImage = useCallback(
			(value: S3Object | null) => {
				onChangeLogo(value!);
			},
			[onChangeLogo],
		);

		const handleChangeLogoSize = useCallback(
			(value: string) => {
				onChangeLogoSize(value as PlainSizeType);
			},
			[onChangeLogoSize],
		);

		const handleChangeIsShowHotelName = useCallback(
			(value: boolean) => {
				onChangeIsShowHotelName(value);
			},
			[onChangeIsShowHotelName],
		);

		const handleChangeIsShowHotelCatchCopy = useCallback(
			(value: boolean) => {
				onChangeIsShowHotelCatchCopy(value);
			},
			[onChangeIsShowHotelCatchCopy],
		);

		const handleChangeIsShowHotelLogo = useCallback(
			(value: boolean) => {
				onChangeIsShowHotelLogo(value);
			},
			[onChangeIsShowHotelLogo],
		);

		const handleChangeIsShowHotelKeyVisual = useCallback(
			(value: boolean) => {
				onChangeIsShowHotelKeyVisual(value);
			},
			[onChangeIsShowHotelKeyVisual],
		);

		return (
			<FormIslandSection
				title="基本設定"
				descriptionChildren={
					<div className="flex items-center gap-x-2 text-base">
						<p>CHILLNNを利用して作成された予約ページの事例は</p>
						<ExternalLink
							href="https://www.notion.so/chillnn/6f0d6f268c4c4efe8620e57bf63e4508"
							text="こちら"
						/>
					</div>
				}
			>
				<Accordion labelChildren={<label className="text-[#4c4c4c]">色の設定</label>}>
					<div className="divide-y divide-zinc-200">
						<div className="px-2 py-3">
							<label>背景色</label>

							<InputWithColorPicker
								color={backgroundColor.value}
								errorMessage={backgroundColor.error}
								onChangeColor={onChangeColorBackground}
							/>
						</div>

						<div className="px-2 py-3">
							<label>テーマカラー</label>

							<InputWithColorPicker
								color={primaryColor.value}
								errorMessage={primaryColor.error}
								onChangeColor={onChangeColorPrimary}
							/>
						</div>

						<div className="px-2 py-3">
							<label>文字色</label>

							<InputWithColorPicker
								color={fontColor.value}
								errorMessage={fontColor.error}
								onChangeColor={onChangeColorFont}
							/>

							<div className="mt-6">
								<p className="text whitespace-pre-wrap text-xs text-caption-50">
									背景色上の文字に反映されます。読みやすい色を選択しましょう。
								</p>
							</div>
						</div>
					</div>

					<div className="mt-5">
						<RecommendColorList onClickRecommendColors={onApplyRecommendationColors} />
					</div>
				</Accordion>

				<Accordion labelChildren={<label className="text-[#4c4c4c]">施設情報の 表示・非表示 設定</label>}>
					<div className="divide-y divide-zinc-200">
						<div className="px-2 py-3">
							<SingleImageField
								label="施設ロゴ"
								image={
									logoImage
										? ({
												bucket: logoImage?.bucket?.value,
												key: logoImage?.key?.value,
												region: logoImage?.region?.value,
												mimeType: logoImage?.mimeType?.value,
												fileName: logoImage?.fileName?.value,
											} satisfies S3Object)
										: null
								}
								onInputImage={handleChangeLogoImage}
								ownerID={hotelID}
								resourceID=""
								errorMessage={logoImage?.bucket.error}
							/>

							<div className="w-full px-2 py-3 xl:w-1/3">
								<SelectField
									label="施設ロゴのサイズ"
									value={logoSize}
									options={LogoSizeOptions}
									handleValueChange={handleChangeLogoSize}
								/>
							</div>

							<div className="px-2 py-3">
								<CheckboxField
									value={isShowHotelLogo}
									label="施設ロゴを表示する"
									handleChange={handleChangeIsShowHotelLogo}
								/>
							</div>
						</div>

						<div className="px-2 py-3">
							<CheckboxField
								value={isShowHotelKeyVisual}
								label="キービジュアルを表示する"
								handleChange={handleChangeIsShowHotelKeyVisual}
							/>
						</div>

						<div className="px-2 py-3">
							<CheckboxField
								value={isShowHotelName}
								label="施設名を表示する"
								handleChange={handleChangeIsShowHotelName}
							/>
						</div>

						<div className="px-2 py-3">
							<CheckboxField
								value={isShowHotelCatchCopy}
								label="キャッチコピーを表示する"
								handleChange={handleChangeIsShowHotelCatchCopy}
							/>
						</div>
					</div>
				</Accordion>

				<Accordion labelChildren={<label className="text-[#4c4c4c]">フォントの設定</label>}>
					<div className="space-y-3 text-center">
						<div className="flex flex-wrap justify-center gap-6">
							<FontCard
								target={PlainFontFamily.KleeOne}
								current={plainFontFamily}
								onClickCard={onChangeFontFamily}
							/>

							<FontCard
								target={PlainFontFamily.NotoSansJp}
								current={plainFontFamily}
								onClickCard={onChangeFontFamily}
							/>

							<FontCard
								target={PlainFontFamily.ShipporiMincho}
								current={plainFontFamily}
								onClickCard={onChangeFontFamily}
							/>
						</div>

						<p className="text whitespace-pre-wrap text-xs text-caption-50">
							ホテル名、スナックのタイトル、プラン一覧、部屋一覧の文言に反映されます。
						</p>
					</div>
				</Accordion>

				<Accordion labelChildren={<label className="text-[#4c4c4c]">予約パーツの設定</label>}>
					<div className="flex flex-wrap justify-center gap-6">
						<LikeReservationComponentCard
							primaryColor={primaryColor.value}
							target={PlainReservationComponentType.Inline}
							current={reservationComponentType}
							onClickCard={onChangeReservationComponentType}
						/>

						<LikeReservationComponentCard
							primaryColor={primaryColor.value}
							target={PlainReservationComponentType.InlineCalendar}
							current={reservationComponentType}
							onClickCard={onChangeReservationComponentType}
						/>
					</div>
				</Accordion>
			</FormIslandSection>
		);
	},
);
